import {
  Box as ChakraBox,
  HStack as ChakraHStack,
  VStack as ChakraVStack,
} from "@chakra-ui/react";
import { forwardRef } from "react";

// All `themeAware` defaults route through CSS variables (set in theme/system.ts)
// so light/dark mode flows automatically without the consumer juggling
// `{ base, _dark }` objects.
const themedDefaults = {
  bg: "var(--chakra-colors-bg-panel)",
  color: "var(--text-primary)",
  borderColor: "var(--card-border)",
} as const;

export interface BoxProps extends React.ComponentProps<typeof ChakraBox> {
  themeAware?: boolean;
}

export const Box = forwardRef<HTMLDivElement, BoxProps>(
  ({ themeAware = false, ...props }, ref) => {
    const themedProps = themeAware
      ? {
          bg: props.bg ?? themedDefaults.bg,
          color: props.color ?? themedDefaults.color,
          borderColor: props.borderColor ?? themedDefaults.borderColor,
          ...props,
        }
      : props;

    return <ChakraBox ref={ref} {...themedProps} />;
  },
);

Box.displayName = "Box";

export interface StackProps extends React.ComponentProps<typeof ChakraHStack> {
  themeAware?: boolean;
}

export const HStack = forwardRef<HTMLDivElement, StackProps>(
  ({ themeAware = false, ...props }, ref) => {
    const themedProps = themeAware
      ? {
          bg: props.bg ?? themedDefaults.bg,
          color: props.color ?? themedDefaults.color,
          borderColor: props.borderColor ?? themedDefaults.borderColor,
          ...props,
        }
      : props;

    return <ChakraHStack ref={ref} {...themedProps} />;
  },
);

HStack.displayName = "HStack";

export interface VStackProps extends React.ComponentProps<typeof ChakraVStack> {
  themeAware?: boolean;
}

export const VStack = forwardRef<HTMLDivElement, VStackProps>(
  ({ themeAware = false, ...props }, ref) => {
    const themedProps = themeAware
      ? {
          bg: props.bg ?? themedDefaults.bg,
          color: props.color ?? themedDefaults.color,
          borderColor: props.borderColor ?? themedDefaults.borderColor,
          ...props,
        }
      : props;

    return <ChakraVStack ref={ref} {...themedProps} />;
  },
);

VStack.displayName = "VStack";
